import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import { Eye, EyeOff, BookOpen, TrendingUp, Award, Users, Shield } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const location = useLocation();

  // Check if this is admin login route
  const isAdminLogin = location.pathname === '/admin/login';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(email, password);
    } catch (error) {
      // Error is handled in the login function
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex">
      {/* Left Side - Branding & Features */}
      <div className={`hidden lg:flex lg:w-1/2 ${isAdminLogin ? 'bg-gradient-to-br from-secondary-700 to-secondary-900' : 'bg-gradient-to-br from-primary-600 to-primary-800'} p-12 text-white relative overflow-hidden`}>
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative z-10 flex flex-col justify-center max-w-lg">
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                {isAdminLogin ? <Shield className="h-7 w-7 text-white" /> : <BookOpen className="h-7 w-7 text-white" />}
              </div>
              <div>
                <h1 className="text-2xl font-bold">{isAdminLogin ? 'Admin Panel' : 'Mock Test Nation'}</h1>
                <p className="text-primary-100">{isAdminLogin ? 'Management System' : 'Excellence Platform'}</p>
              </div>
            </div>
            <h2 className="text-4xl font-bold mb-4 leading-tight">
              {isAdminLogin ? (
                <>
                  Manage Platform
                  <span className="block gradient-text-accent">With Ease</span>
                </>
              ) : (
                <>
                  Master JEE with
                  <span className="block gradient-text-accent">Smart Practice</span>
                </>
              )}
            </h2>
            <p className="text-xl text-primary-100 leading-relaxed">
              {isAdminLogin 
                ? 'Access comprehensive admin tools to manage users, tests, and monitor platform performance.'
                : 'Join thousands of students who\'ve achieved their JEE dreams with our AI-powered mock tests and personalized analytics.'
              }
            </p>
          </div>

          <div className="space-y-6">
            {isAdminLogin ? (
              <>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                    <Users className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">User Management</h3>
                    <p className="text-primary-100">Monitor and manage student accounts and activities</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                    <BookOpen className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Test Management</h3>
                    <p className="text-primary-100">Create and manage mock tests and question banks</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                    <TrendingUp className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Analytics Dashboard</h3>
                    <p className="text-primary-100">Track platform performance and user engagement</p>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                    <TrendingUp className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Smart Analytics</h3>
                    <p className="text-primary-100">Track your progress with detailed performance insights</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                    <Award className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Gamified Learning</h3>
                    <p className="text-primary-100">Earn badges and maintain streaks to stay motivated</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                    <Users className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Expert Content</h3>
                    <p className="text-primary-100">Questions reviewed by JEE experts and toppers</p>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="mt-12 p-6 bg-white/10 backdrop-blur-sm rounded-2xl">
            <div className="flex items-center justify-between text-center">
              {isAdminLogin ? (
                <>
                  <div>
                    <div className="text-2xl font-bold">50K+</div>
                    <div className="text-sm text-primary-100">Users</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">1K+</div>
                    <div className="text-sm text-primary-100">Tests</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">99.9%</div>
                    <div className="text-sm text-primary-100">Uptime</div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <div className="text-2xl font-bold">50K+</div>
                    <div className="text-sm text-primary-100">Students</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">95%</div>
                    <div className="text-sm text-primary-100">Success Rate</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">10K+</div>
                    <div className="text-sm text-primary-100">Questions</div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="lg:hidden mb-6">
              <div className={`w-16 h-16 ${isAdminLogin ? 'bg-gradient-to-r from-secondary-600 to-secondary-700' : 'bg-gradient-to-r from-primary-600 to-primary-700'} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                {isAdminLogin ? <Shield className="h-8 w-8 text-white" /> : <BookOpen className="h-8 w-8 text-white" />}
              </div>
            </div>
            <h2 className="text-3xl font-bold text-secondary-900 mb-2">
              {isAdminLogin ? 'Admin Login' : 'Welcome back!'}
            </h2>
            <p className="text-secondary-600">
              {isAdminLogin 
                ? 'Access the admin dashboard to manage the platform'
                : 'Sign in to continue your JEE preparation journey'
              }
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="input"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  className="input pr-12"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center pr-4 text-secondary-400 hover:text-secondary-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

           

            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
                />
                <span className="ml-2 text-sm text-secondary-600">Remember me</span>
              </label>
              {!isAdminLogin && (
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-primary-600 hover:text-primary-500 transition-colors"
                >
                  Forgot password?
                </Link>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full btn-lg"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Signing in...
                </div>
              ) : (
                isAdminLogin ? 'Access Admin Panel' : 'Sign In'
              )}
            </button>
          </form>

          <div className="mt-8 text-center space-y-3">
            {!isAdminLogin && (
              <>
                <p className="text-secondary-600">
                  Don't have an account?{' '}
                  <Link
                    to="/register"
                    className="font-semibold text-primary-600 hover:text-primary-500 transition-colors"
                  >
                    Create one now
                  </Link>
                </p>
                <div className="text-secondary-400">•</div>
                <Link
                  to="/admin/login"
                  className="text-sm text-secondary-600 hover:text-secondary-700 flex items-center justify-center"
                >
                  <Shield className="h-4 w-4 mr-1" />
                  Admin Login
                </Link>
              </>
            )}
            
            {isAdminLogin && (
              <Link
                to="/login"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                ← Back to Student Login
              </Link>
            )}
          </div>

          {!isAdminLogin && (
            <div className="mt-8 pt-6 border-t border-secondary-200">
              <div className="text-center">
                <p className="text-xs text-secondary-500 mb-4">Trusted by students from</p>
                <div className="flex items-center justify-center space-x-6 text-secondary-400">
                  <span className="font-semibold">IIT</span>
                  <span className="font-semibold">NIT</span>
                  <span className="font-semibold">IIIT</span>
                  <span className="font-semibold">BITS</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}